import graphqlPost from "api/graphqlClient";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { SchemaForm } from "components/Form/components/SchemaForm/SchemaForm";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { isEqual } from "lodash";
import { SubmitLock } from "components/Form/components/SubmitLock/SubmitLock";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import strings from "utils/strings";
import { WidgetComponentProps } from "../types";
import { seedValues } from "./runFlowWidgetSeed";

/**
 * Config for the `run_flow_with_inputs` widget — renders a chosen flow's
 * `input_schema` as a full nested schema form (reusing {@link SchemaForm}) and, on
 * submit, creates an execution of that flow with the entered values as
 * `input_value`.
 *
 * Complements {@link ActionButtonWidget} (which runs a flow with STATIC inputs) and
 * {@link RecordFormWidget} (a flat field list that creates a record). Its first use
 * is the operator dashboard's "new one-line ask" box, pointed at the
 * `operator-intake-create` flow so the intake's `from_person` can be auto-detected
 * from the created execution's `created_by`.
 */
export interface RunFlowWithInputsWidgetConfig {
    /** Id of the flow whose `input_schema` is rendered and which is executed on submit. */
    flow_id: string;
    /** Label on the submit button. Defaults to "Run". */
    submit_label?: string;
    /** When true, navigate to the created execution after submit. Defaults to false. */
    navigate_to_execution?: boolean;
    /**
     * Static input values merged UNDER the form values (form values win on key
     * collision). Useful for constants the user should not edit.
     */
    fixed_input?: Record<string, unknown>;
    /** Success notification text. Defaults to "Started execution.". */
    success_message?: string;
    /** Reset the form to its schema defaults after a successful submit. Defaults to true. */
    reset_on_success?: boolean;
}

interface LastExecution {
    id: string;
    created_at: string;
}

/**
 * `run_flow_with_inputs` widget — a full input-schema form that runs a flow.
 *
 * Fetches the flow's `input_schema` over GraphQL, renders it with {@link SchemaForm}
 * (nested fields, arrays, datatype checks, required, YAML fallback — the same
 * component the record editor uses), and posts to the REST `execution` endpoint on
 * submit (same call as {@link ActionButtonWidget}). On success it shows a
 * notification and a link to open the created execution.
 */
export const RunFlowWithInputsWidget = ({
    config,
}: WidgetComponentProps<RunFlowWithInputsWidgetConfig>) => {
    const flowId = config?.flow_id;
    const submitLabel = config?.submit_label ?? "Run";
    const navigateAfter = config?.navigate_to_execution ?? false;
    const resetOnSuccess = config?.reset_on_success ?? true;

    const history = useHistory();

    const [schema, setSchema] = useState<unknown>(null);
    const [values, setValues] = useState<Record<string, any>>({});
    const [isLoadingSchema, setLoadingSchema] = useState(false);
    const [schemaError, setSchemaError] = useState<string | null>(null);
    const [isRunning, setRunning] = useState(false);
    const [lastExecution, setLastExecution] = useState<LastExecution | null>(null);
    // Bumped to force-remount the SchemaForm on reset: SchemaForm seeds its internal
    // form state from inputProps.value.values only at mount, so a plain value-prop
    // change is otherwise ignored and the fields would keep the submitted values.
    const [formKey, setFormKey] = useState(0);
    // Set true right before OUR OWN programmatic navigation (navigate_to_execution)
    // so the unsaved-changes guard does not block a navigation we initiated.
    const [isNavigationAllowed, setNavigationAllowed] = useState(false);
    const [pendingNavUrl, setPendingNavUrl] = useState<string | null>(null);

    // Fetch the flow's input_schema and seed initial form values from it.
    useEffect(() => {
        if (!flowId) {
            return;
        }
        let cancelled = false;
        setLoadingSchema(true);
        setSchemaError(null);
        graphqlPost({
            query: `query ($id: String!) { flow(id: $id) { input_schema } }`,
            variables: { id: flowId },
        })
            .then((data: any) => {
                if (cancelled) {
                    return;
                }
                const inputSchema = data?.data?.flow?.input_schema ?? null;
                setSchema(inputSchema);
                setValues(seedValues(inputSchema));
            })
            .catch((err: any) => {
                if (cancelled) {
                    return;
                }
                setSchemaError("Failed to load the flow's input schema.");
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Failed to load flow input schema"
                        error={err}
                    />
                );
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingSchema(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [flowId]);

    const inputProps = useMemo(
        () => ({
            value: { values, schema: schema as any },
            onChange: (next: { values: any; schema: unknown }) => {
                setValues(next?.values ?? {});
            },
        }),
        [values, schema]
    );

    const formAttributes = useMemo(
        () => ({ isNewRecord: true, canUpdateFieldOnSchemaChanges: true }),
        []
    );

    // The form's pristine baseline (schema defaults). "Dirty" == the user has entered
    // values that differ from it — drives both the unsaved-changes guard and the
    // reset. While the schema is still loading (null) both sides are {} → not dirty.
    const defaultValues = useMemo(() => seedValues(schema), [schema]);
    const isDirty = useMemo(() => !isEqual(values, defaultValues), [values, defaultValues]);

    // Reset the form back to its schema defaults. Bumping formKey remounts the
    // SchemaForm so it re-seeds from the fresh (default) values (see formKey above).
    const resetForm = useCallback(() => {
        setValues(seedValues(schema));
        setFormKey((k) => k + 1);
    }, [schema]);

    // Perform an OUR-OWN navigation (navigate_to_execution) once state has settled.
    // Deferring to an effect lets the render that disarms the guard commit first, so
    // the BlockingNavigationModal's <Prompt> is already gone and won't block us.
    useEffect(() => {
        if (pendingNavUrl) {
            history.push(pendingNavUrl);
            setPendingNavUrl(null);
        }
    }, [pendingNavUrl, history]);

    const handleSubmit = useCallback(async () => {
        if (!flowId) {
            NotificationService.error("run_flow_with_inputs widget: flow_id is not configured.");
            return;
        }
        setRunning(true);
        try {
            const inputValue = { ...(config?.fixed_input ?? {}), ...(values ?? {}) };
            const response = await restClient.post(
                "execution",
                {
                    type: "FLOW",
                    flow_id: flowId,
                    input_value: inputValue,
                    is_productive: true,
                    use_breakpoints: false,
                },
                { trackAction: true }
            );
            const executionId = response?.data?.id;
            if (navigateAfter && executionId) {
                NotificationService.success(
                    `Started execution${executionId ? ` ${executionId.slice(0, 8)}…` : ""}.`
                );
                // Clear the form and disarm the unsaved-changes guard BEFORE navigating
                // to the created execution, so our own navigation isn't blocked.
                resetForm();
                setNavigationAllowed(true);
                setPendingNavUrl(getRelativeUrl("execution", executionId));
                return;
            }
            NotificationService.success(config?.success_message ?? "Started execution.");
            if (executionId) {
                setLastExecution({
                    id: executionId,
                    created_at: response?.data?.created_at ?? new Date().toISOString(),
                });
            }
            if (resetOnSuccess) {
                resetForm();
            }
        } catch (err: any) {
            NotificationService.error(<RestErrorNotification error={err} />);
        } finally {
            setRunning(false);
        }
    }, [
        flowId,
        config?.fixed_input,
        config?.success_message,
        values,
        navigateAfter,
        resetOnSuccess,
        resetForm,
    ]);

    if (!flowId) {
        return <div className="DashboardWidget__error">flow_id is not configured.</div>;
    }

    const executionLink = lastExecution ? getRelativeUrl("execution", lastExecution.id) : null;

    return (
        <div className="DashboardRunFlowWithInputs" style={{ position: "relative" }}>
            <SpinnerAnimation isLoading={isLoadingSchema} />
            {schemaError ? (
                <div className="DashboardWidget__error">{schemaError}</div>
            ) : schema != null ? (
                // Only mount the SchemaForm once a real schema is loaded: SchemaForm
                // (SchemaFormFragment) throws "Invalid schema" on a null/undefined schema,
                // which the error boundary logs to the console on every render. `schema`
                // is null both on the initial render before the input_schema fetch resolves
                // AND for flows that genuinely have no inputs.
                <SubmitLock locked={isRunning}>
                    <SchemaForm key={formKey} inputProps={inputProps} attributes={formAttributes} />
                </SubmitLock>
            ) : !isLoadingSchema ? (
                // Schema loaded but the flow declares no inputs — just show the run button.
                <div className="DashboardRunFlowWithInputs__noInputs">
                    This flow has no inputs.
                </div>
            ) : null}
            {isDirty && !isNavigationAllowed && (
                <BlockingNavigationModal
                    navigate={history.push}
                    shouldBlockNavigation
                    titleText={strings.navigateAwayUnsavedPrompt}
                />
            )}
            <div className="DashboardRunFlowWithInputs__actions">
                <Button
                    variation="primary"
                    label={submitLabel}
                    icon={IconCollection.play}
                    loading={isRunning}
                    disabled={isRunning || isLoadingSchema || !!schemaError}
                    onClick={handleSubmit}
                />
                {!navigateAfter && lastExecution && executionLink && (
                    <div className="DashboardRunFlowWithInputs__result">
                        <TimeAgo
                            className="DashboardRunFlowWithInputs__time"
                            datetime={lastExecution.created_at}
                        />
                        <a
                            href={executionLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="DashboardRunFlowWithInputs__link"
                        >
                            Open execution
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};
